import { Box, Link, Typography } from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import installDayIcon from "../../../../assets/icons/tracker/install-day.svg";
import installScheduledIcon from "../../../../assets/icons/tracker/install-scheduled.svg";
import { SELF_INSTALL_STATUS_KEYS } from "../../../../Constants/constants";
import { addSpaceAfterComma, getTechTrackerStatusContent } from "../../../../Services/helper";
import { TrackerSteps } from "./TrackerSteps";

type SelfInstallOrderDetails = {
  orderStatus?: string | null;
  dropshipInfo?: {
    shippingAddress?: string | null;
    trackingURL?: string | null;
    shippingDate?: string | null;
    model?: string | null;
  } | null;
};

// Self-install steps keyed by normalised order status (same map as the mobile app). There are two
// steps only, so a completed self-install order stays on Equipment Shipped.
const SELF_INSTALL_STEPS: Record<string, number> = {
  orderplaced: 0,
  installationscheduled: 0,
  equipmentshipped: 1,
  installday: 1,
  selfinstallcomplete: 1,
  installcomplete: 1,
};

const EQUIPMENT_SHIPPED_STEP = 1;

// Fallback step labels/icons and status copy, used when the "where-is-my-tech" banner has none or
// its icon fails to load. Each step shows the banner's icon, like the mobile app; the fallbacks are
// the same images the banner sends (a wrench for Order Placed, a calendar for Equipment Shipped),
// so a step looks the same whether or not the CMS icon loads.
const steps = [
  { label: "Order Placed", icon: installDayIcon },
  { label: "Equipment Shipped", icon: installScheduledIcon },
];

// Equipment Shipped always uses this heading and no description, never the CMS copy (its step
// label and icon still come from the CMS), same as the mobile app.
const defaultStatusContent: { heading: string; subheading?: string }[] = [
  {
    heading: "Your order has been placed successfully",
    subheading: "Your order has been placed successfully",
  },
  {
    heading: "Your equipment is on the way",
  },
];

// dropshipInfo values as trimmed text; null, blank and non-text values come back empty, so their
// rows stay hidden.
const toText = (value: unknown) =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";

const formatShippingDate = (value: string) => {
  const parsedValue = moment.utc(value);
  return parsedValue.isValid() ? parsedValue.format("MMM D, YYYY") : value;
};

// Only web links are rendered, so a malformed or non-http tracking URL hides the link.
const getTrackingUrl = (value: unknown) => {
  const url = toText(value);
  return /^https?:\/\/\S+$/i.test(url) ? url : "";
};

const linkSx = { color: "#006ce5", fontSize: 14, fontWeight: 700 };

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ pb: 1.75 }}>
    <Typography component="p" sx={{ mb: 0.5, color: "#666", fontSize: 12, lineHeight: 1.3 }}>
      {label}
    </Typography>
    <Typography
      component="p"
      sx={{ m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4, overflowWrap: "anywhere" }}>
      {value}
    </Typography>
  </Box>
);

// Tracker for self-install (dropship) orders, ported from the mobile SelfInstallStatusTracker:
// three steps, a status card, and a delivery card built from the order's dropshipInfo.
export const SelfInstallStatusTracker = ({
  orderDetails,
}: {
  orderDetails?: SelfInstallOrderDetails | null;
}) => {
  const bannerMessages = useSelector((state: any) => state?.sideCoreContent?.bannerMessages);
  const normalizedStatus = String(orderDetails?.orderStatus ?? "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
  const activeStep = SELF_INSTALL_STEPS[normalizedStatus] ?? 0;
  const stepContent = SELF_INSTALL_STATUS_KEYS.map((key) =>
    getTechTrackerStatusContent(bannerMessages, key)
  );
  const trackerSteps = steps.map((step, index) => ({
    label: stepContent[index]?.orderStatusLabel || step.label,
    icon: stepContent[index]?.iconSrc || step.icon,
    fallbackIcon: step.icon,
  }));
  const cmsContent = activeStep === EQUIPMENT_SHIPPED_STEP ? null : stepContent[activeStep];
  const heading = cmsContent?.heading || defaultStatusContent[activeStep]?.heading;
  const subheading = cmsContent?.subheading || defaultStatusContent[activeStep]?.subheading;

  const dropshipInfo = orderDetails?.dropshipInfo;
  const shippingAddress = toText(dropshipInfo?.shippingAddress);
  const shippingDate = toText(dropshipInfo?.shippingDate);
  const model = toText(dropshipInfo?.model);
  const trackingURL = getTrackingUrl(dropshipInfo?.trackingURL);
  const showDeliveryCard = Boolean(shippingAddress || shippingDate || model || trackingURL);

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        border: "1px solid #dedede",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}>
      <TrackerSteps
        steps={trackerSteps}
        activeStep={activeStep}
        ariaLabel="Self install status"
        iconSize={36}
      />

      <Box sx={{ px: 2.25, py: 2.25, backgroundColor: "#f1f6ff" }}>
        <Typography
          component="h2"
          sx={{
            mb: subheading ? 1 : 0,
            color: "#171717",
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.25,
          }}>
          {heading}
        </Typography>
        {subheading && (
          <Typography
            component="p"
            sx={{ maxWidth: 560, m: 0, color: "#171717", fontSize: 14, lineHeight: 1.4 }}>
            {subheading}
          </Typography>
        )}
      </Box>

      {showDeliveryCard && (
        <Box sx={{ px: 2, pt: 1.75, backgroundColor: "#fff" }}>
          {shippingAddress && (
            <DetailRow label="Delivery address" value={addSpaceAfterComma(shippingAddress)} />
          )}
          {shippingDate && (
            <DetailRow label="Shipment date" value={formatShippingDate(shippingDate)} />
          )}
          {model && <DetailRow label="Model" value={model} />}
          {trackingURL && (
            <Box
              sx={{
                mx: -2,
                px: 2,
                py: 1.5,
                borderTop: shippingAddress || shippingDate || model ? "1px solid #e7e7e7" : "none",
              }}>
              <Link
                href={trackingURL}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                sx={linkSx}>
                TRACK YOUR SHIPMENT
              </Link>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
